import { de, enGB, es, fr, it } from 'date-fns/locale'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, type LinkProps, type To } from 'react-router'

export const i18n = {
	// This is the list of languages your application supports
	supportedLngs: ['en', 'es', 'fr', 'de', 'it'] as const,
	// This is the language you want to use in case
	// if the user language is not in the supportedLngs
	fallbackLng: 'en',

	// The default namespace of i18next is "translation", but you can customize it here
	defaultNS: 'common',
	// Disabling suspense is recommended
	react: { useSuspense: true },
}

export type ISupportedLanguage = (typeof i18n.supportedLngs)[number]

export function useChangeLanguage(locale: string) {
	const { i18n } = useTranslation()
	useEffect(() => {
		void i18n.changeLanguage(locale)
	}, [locale, i18n])
}

export const useLocale = () => {
	const { i18n } = useTranslation()

	return i18n.language as ISupportedLanguage
}

export const useDateFnsLocale = () => {
	const locale = useLocale()
	const getLocale = () => {
		switch (locale) {
			case 'en':
				return enGB
			case 'es':
				return es
			case 'de':
				return de
			case 'fr':
				return fr
			case 'it':
				return it
			default:
				return enGB
		}
	}

	return getLocale()
}

export function LocalizedLink(props: Omit<LinkProps, 'to'> & { to: To }) {
	const locale = useLocale()

	if (typeof props.to === 'string' && props.to.startsWith('http')) {
		return <a {...props} href={props.to} />
	}

	if (typeof props.to === 'string' && props.to.startsWith('/')) {
		return <Link viewTransition {...props} to={`/${locale}${props.to}`} />
	}

	if (typeof props.to !== 'string' && props.to?.pathname?.startsWith('/')) {
		return (
			<Link
				viewTransition
				{...props}
				to={{
					...props.to,
					pathname: `/${locale}${props.to.pathname}`,
				}}
			/>
		)
	}

	return <Link viewTransition {...props} />
}

export const getUppercaseLocale = (locale: ISupportedLanguage) => {
	return locale.toUpperCase() as Uppercase<ISupportedLanguage>
}
export const getOGLocales = (locale: ISupportedLanguage) => {
	const alternateLocales = i18n.supportedLngs.filter((l) => l !== locale)
	return [
		{
			name: 'og:locale',
			content: `${locale}_${getUppercaseLocale(locale)}`,
		},
		...alternateLocales.map((l) => ({
			name: 'og:locale:alternate',
			content: `${l}_${getUppercaseLocale(l)}`,
		})),
	]
}

export const getFullLocale = (locale: ISupportedLanguage) => {
	switch (locale) {
		case 'en':
			return 'English'
		case 'es':
			return 'Español'
		case 'fr':
			return 'Français'
		case 'de':
			return 'Deutsch'
		case 'it':
			return 'Italiano'
	}
}

export const getSpanishFullLocale = (locale: ISupportedLanguage) => {
	switch (locale) {
		case 'en':
			return 'Inglés'
		case 'es':
			return 'Español'
		case 'fr':
			return 'Francés'
		case 'de':
			return 'Alemán'
		case 'it':
			return 'Italiano'
	}
}
